/**
 * OAuthConnect Page
 * Handles OAuth connection initiation with platform selection
 */

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { OAuthApiClient } from '../lib/oauth/OAuthApiClient';
import { PlatformLogo, getPlatformName } from '../components/icons/PlatformLogos';
import type { PlatformKey } from '../components/icons/PlatformLogos';
import type { Platform } from '../lib/oauth/types';

export const OAuthConnect: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentOrganization, switchOrganization } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const [availablePlatforms, setAvailablePlatforms] = useState<Platform[]>([]);
  const [loadingPlatforms, setLoadingPlatforms] = useState(true);

  // Get params from URL
  const platform = searchParams.get('platform') as Platform | null;
  const orgId = searchParams.get('org_id');
  const redirectUri = searchParams.get('redirect_uri') || `${window.location.origin}/oauth/callback`;

  const oauthClient = new OAuthApiClient();

  // Track attempted platform/orgId combinations to prevent infinite loops
  // When user clicks "Try Again", page reloads which would trigger useEffect again
  // This ref ensures we only auto-initiate once per unique platform/orgId pair
  const attemptedRef = useRef<string | null>(null);

  // Fetch available platforms on component mount
  useEffect(() => {
    const fetchAvailablePlatforms = async () => {
      try {
        setLoadingPlatforms(true);
        const platforms = await oauthClient.getPlatforms();
        setAvailablePlatforms(platforms);
      } catch (err) {
        console.error('Failed to fetch available platforms:', err);
        // Fallback to no platforms to prevent showing unsupported ones
        setAvailablePlatforms([]);
      } finally {
        setLoadingPlatforms(false);
      }
    };

    fetchAvailablePlatforms();
  }, []);

  useEffect(() => {
    // If platform is specified, initiate OAuth (with org switch if needed)
    if (platform && orgId) {
      // Only initiate if this is a NEW platform/orgId combination
      // This prevents infinite loops when user clicks "Try Again" after an error
      const attemptKey = `${platform}-${orgId}`;
      if (attemptedRef.current !== attemptKey) {
        attemptedRef.current = attemptKey;
        initiateOAuthWithOrgSwitch(platform, orgId);
      }
    }
  }, [platform, orgId]);

  /**
   * Initiate OAuth flow with organization context switch
   */
  const initiateOAuthWithOrgSwitch = async (selectedPlatform: Platform, targetOrgId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Check if we need to switch organization context
      if (currentOrganization?.id !== targetOrgId) {
        setSwitchingOrg(true);

        // Switch to target organization to update JWT
        await switchOrganization(targetOrgId);

        // Emit organization changed event for other components
        window.dispatchEvent(
          new CustomEvent('organizationChanged', {
            detail: { organizationId: targetOrgId },
          })
        );

        setSwitchingOrg(false);
      }

      // Now initiate OAuth with correct org context in JWT
      await initiateOAuth(selectedPlatform);
    } catch (err) {
      console.error('Failed to switch organization or initiate OAuth:', err);
      setError(err instanceof Error ? err.message : 'Failed to initiate OAuth connection');
      setLoading(false);
      setSwitchingOrg(false);
    }
  };

  /**
   * Initiate OAuth flow
   */
  const initiateOAuth = async (selectedPlatform: Platform) => {
    try {
      // Call OAuth initiation endpoint (JWT now has correct org_id)
      const result = await oauthClient.initiate(selectedPlatform, redirectUri);

      // Redirect to platform authorization URL
      window.location.href = result.authorization_url;
    } catch (err) {
      console.error('Failed to initiate OAuth:', err);
      setError(err instanceof Error ? err.message : 'Failed to initiate OAuth connection');
      setLoading(false);
    }
  };

  /**
   * Handle platform selection
   */
  const handlePlatformSelect = (selectedPlatform: Platform) => {
    if (!orgId) {
      setError('Organization ID is missing');
      return;
    }

    // Clear error from previous attempt
    setError(null);

    // Update URL with platform and initiate
    const newUrl = `/oauth/connect?platform=${selectedPlatform}&org_id=${orgId}&redirect_uri=${encodeURIComponent(redirectUri)}`;
    navigate(newUrl, { replace: true });

    // Initiate OAuth flow (with org switch if needed)
    // Note: useEffect will handle the actual initiation when URL params change
    // This ensures proper async flow and prevents race conditions
  };

  /**
   * Get platform icon using shared module
   */
  const getPlatformIcon = (platformName: Platform) => (
    <PlatformLogo platform={platformName as PlatformKey} size="lg" />
  );

  /**
   * Get platform description
   */
  const getPlatformDescription = (platformName: Platform): string => {
    const descriptions: Record<Platform, string> = {
      meta: 'Connect your Facebook and Instagram ad accounts',
      google_ads: 'Connect your Google Ads accounts',
      tiktok: 'Connect your TikTok ad accounts',
      twitter: 'Connect your Twitter (X) ad accounts',
      pinterest: 'Connect your Pinterest ad accounts',
    };
    return descriptions[platformName];
  };

  // If platform is specified and OAuth is being initiated
  if (platform && loading) {
    return (
      <div className="min-h-screen bg-ground dark:bg-background-dark flex items-center justify-center p-4">
        <div className="bg-card dark:bg-card-dark rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">{getPlatformIcon(platform)}</div>

          <div className="flex justify-center items-center mb-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>

          {switchingOrg ? (
            <>
              <h2 className="text-2xl font-bold text-ink mb-2">
                Switching Organization...
              </h2>
              <p className="text-ink-2">
                Updating organization context for OAuth connection
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-ink mb-2">
                Connecting to {getPlatformName(platform)}
              </h2>
              <p className="text-ink-2">
                You will be redirected to {getPlatformName(platform)} to authorize access...
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // If error occurred
  if (error) {
    return (
      <div className="min-h-screen bg-ground dark:bg-background-dark flex items-center justify-center p-4">
        <div className="bg-card dark:bg-card-dark rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-bad-soft rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-bad"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-ink mb-4 text-center">
            Connection Failed
          </h2>
          <p className="text-ink-2 mb-6 text-center">{error}</p>

          <div className="flex gap-3">
            <button
              onClick={() => navigate('/settings')}
              className="flex-1 px-4 py-2 bg-inset text-ink rounded-lg hover:bg-gray-300 transition-colors"
            >
              Back to Settings
            </button>
            <button
              onClick={() => {
                // Navigate back to platform selection by clearing platform param
                // This resets the component state and allows user to select again
                navigate(`/oauth/connect?org_id=${orgId}&redirect_uri=${encodeURIComponent(redirectUri)}`, { replace: true });
              }}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Try Different Platform
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while fetching available platforms
  if (loadingPlatforms) {
    return (
      <div className="min-h-screen bg-ground dark:bg-background-dark flex items-center justify-center p-4">
        <div className="bg-card dark:bg-card-dark rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="flex justify-center items-center mb-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
          <h2 className="text-2xl font-bold text-ink mb-2">
            Loading Platforms...
          </h2>
          <p className="text-ink-2">
            Checking available platforms
          </p>
        </div>
      </div>
    );
  }

  // Show message if no platforms are available
  if (availablePlatforms.length === 0) {
    return (
      <div className="min-h-screen bg-ground dark:bg-background-dark flex items-center justify-center p-4">
        <div className="bg-card dark:bg-card-dark rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-warn-soft rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-warn"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4v2m0 4v2M6.343 6.343l1.414 1.414M17.657 17.657l1.414 1.414M6.343 17.657l-1.414-1.414M17.657 6.343l-1.414-1.414"
                />
              </svg>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-ink mb-4 text-center">
            No Platforms Available
          </h2>
          <p className="text-ink-2 mb-6 text-center">
            Please contact support or an administrator to enable platform connections.
          </p>

          <button
            onClick={() => navigate('/settings')}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Back to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ground dark:bg-background-dark flex items-center justify-center p-4">
      <div className="bg-card dark:bg-card-dark rounded-lg shadow-lg p-8 max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-[34px] md:text-display-m font-extrabold leading-none tracking-[-0.035em] text-ink mb-3">
            Connect Platform Account
          </h1>
          <p className="text-ink-2">
            Choose a platform to connect your advertising account
          </p>
        </div>

        {/* Platform Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {availablePlatforms.map((p) => (
            <button
              key={p}
              onClick={() => handlePlatformSelect(p)}
              disabled={loading}
              className="flex flex-col items-center gap-4 p-6 bg-ground border-2 border-line dark:border-border-dark rounded-lg hover:border-primary dark:hover:border-primary transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {getPlatformIcon(p)}
              <div className="text-center">
                <p className="font-semibold text-ink group-hover:text-primary mb-1">
                  {getPlatformName(p)}
                </p>
                <p className="text-xs text-ink-3">
                  {getPlatformDescription(p)}
                </p>
              </div>
              <svg
                className="w-5 h-5 text-ink-3 group-hover:text-primary transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          ))}
        </div>

        {/* Security Notice */}
        <div className="bg-work-soft border border-work/30 rounded-lg p-4">
          <div className="flex gap-3">
            <svg
              className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">
                Secure OAuth 2.0 Authentication
              </h4>
              <p className="text-xs text-work">
                You'll be redirected to the platform's official authorization page. We never see your
                password - OAuth tokens are encrypted and stored securely.
              </p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/settings')}
            className="text-ink-2 hover:text-primary dark:hover:text-primary transition-colors"
          >
            ← Back to Settings
          </button>
        </div>
      </div>
    </div>
  );
};
